
import React from 'react';
import { Card } from "@/components/ui/card";
import { Target } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import type { PerformanceData } from './types';

interface DifficultyAnalysisProps {
  performanceData: PerformanceData[];
}

export const DifficultyAnalysis: React.FC<DifficultyAnalysisProps> = ({ performanceData }) => {
  const difficultyData = React.useMemo(() => {
    const difficultyStats = performanceData.reduce((acc, curr) => {
      if (!acc[curr.difficulty]) {
        acc[curr.difficulty] = { correct: 0, total: 0 };
      }
      acc[curr.difficulty].correct += curr.correct_answers;
      acc[curr.difficulty].total += curr.total_questions;
      return acc;
    }, {} as Record<string, { correct: number; total: number }>);

    return Object.entries(difficultyStats).map(([difficulty, stats]) => ({
      name: difficulty.charAt(0).toUpperCase() + difficulty.slice(1),
      value: (stats.correct / stats.total) * 100,
      color: difficulty === 'easy' ? '#4ADE80' : difficulty === 'medium' ? '#F59E0B' : '#EF4444'
    }));
  }, [performanceData]);

  // Enhanced custom colors
  const COLORS = ['#4ADE80', '#F59E0B', '#EF4444'];
  const GRADIENT_IDS = ['greenGradient', 'amberGradient', 'redGradient'];

  return (
    <Card className="p-6 bg-gradient-to-br from-purple-50 to-white border border-slate-100 shadow-sm">
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 bg-clip-text text-transparent bg-gradient-to-r from-purple-700 to-indigo-500">
        <Target className="w-5 h-5 text-purple-500" />
        Performance by Difficulty
      </h2>
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <defs>
              <radialGradient id="greenGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                <stop offset="0%" stopColor="#86EFAC" />
                <stop offset="100%" stopColor="#4ADE80" />
              </radialGradient>
              <radialGradient id="amberGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                <stop offset="0%" stopColor="#FCD34D" />
                <stop offset="100%" stopColor="#F59E0B" />
              </radialGradient>
              <radialGradient id="redGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                <stop offset="0%" stopColor="#FCA5A5" />
                <stop offset="100%" stopColor="#EF4444" />
              </radialGradient>
            </defs>
            <Pie
              data={difficultyData}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={100}
              innerRadius={60}
              fill="#8884d8"
              dataKey="value"
              label={({ name, value }) => `${name}: ${value.toFixed(1)}%`}
              paddingAngle={4}
            >
              {difficultyData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={`url(#${GRADIENT_IDS[index % GRADIENT_IDS.length]})`} 
                  stroke="#fff"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value: number) => [`${value.toFixed(1)}%`, 'Accuracy']}
              contentStyle={{ 
                backgroundColor: 'white', 
                borderRadius: '8px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                border: '1px solid #E5E7EB',
                padding: '8px 12px'
              }}
              itemStyle={{ fontWeight: 500 }}
            />
            <Legend 
              verticalAlign="bottom"
              iconType="circle"
              formatter={(value) => {
                const difficulty = value.toLowerCase();
                let textColor;
                if (difficulty === 'easy') textColor = 'text-green-600';
                else if (difficulty === 'medium') textColor = 'text-amber-600';
                else textColor = 'text-red-600';
                
                return <span className={`text-sm ${textColor} font-medium`}>{value}</span>;
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 text-xs text-gray-500 text-center">
        Accuracy rates across different difficulty levels
      </div>
    </Card>
  );
};
